import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import AsideNav from "./AsideNav/AsideNav";
import { IoIosLogOut } from "react-icons/io";
import { toast } from "react-toastify";
import "./AdminDashboard.css";
import "./adminShell.css";
import UserTokenHandler from "../../sharedComponents/UserTokenHandler";
import GetLocalStorageUserId from "../../sharedComponents/GetLocalStorageuserId";
import { redirectToIdentityLogin } from "../../../utils/identityLogin";
import { clearAuthStorage } from "../../../utils/authToken";
import { notifyAuthSessionUpdated } from "../../../utils/authSession";
import axios from "axios";
import { usePermission } from "../../../context/PermissionContext";
import {
  canAccessDeliverySetup,
  screenCodeFromPath,
} from "../../../utils/permissionScreens";
import CrmActivityTracker from "./Crm/CrmActivityTracker";
import { crmActivityLogout } from "../../../api/crmApi";

const titleFromPath = (pathname) => {
  const clean = String(pathname || "").replace(/\/+$/, "");
  if (clean === "/admin-dashboard") return "Dashboard";
  const slug = clean.split("/").filter(Boolean).pop() || "Dashboard";
  return slug
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const AdminDashboard = () => {
  const [userId, setUserId] = useState(null);
  const move = useNavigate();
  const location = useLocation();
  const backendUrl = process.env.REACT_APP_API_URL;
  UserTokenHandler();
  useEffect(() => {
    setUserId(GetLocalStorageUserId());
  }, []);

  const pageTitle = useMemo(
    () => titleFromPath(location.pathname),
    [location.pathname],
  );

  const handleLoginClick = () => {
    redirectToIdentityLogin(window.location.origin);
  };
  const logoutHandle = async () => {
    await crmActivityLogout();
    toast.success("Logout Successfull", {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
    clearAuthStorage();
    notifyAuthSessionUpdated();
    move("/");
  };
  const [isSyncing, setIsSyncing] = useState(false);
  const handleSyncClick = () => {
    if (isSyncing) {
      toast.error("Already Syncing", {
        position: "top-right",
        autoClose: 5000,
        theme: "colored",
      });
      return;
    }
    setIsSyncing(true);
    axios
      .get(
        "https://api.currencyapi.com/v3/latest?apikey=cur_live_8yU35ERIQLgyu2nJ5NFQNRtwb3Z9XVaMTXdygf1j&currencies=BDT%2CUSD%2CCAD%2CBDT&&base_currency=GBP",
      )
      .then((response) => {
        axios
          .post(`${backendUrl}Parcel/ReceiveRates`, response.data)
          .then(() => {
            setIsSyncing(false);
            toast.success("Exchange Rate Sync Successfull", {
              position: "top-right",
              autoClose: 5000,
              theme: "colored",
            });
          })
          .catch(() => {
            setIsSyncing(false);
            toast.error("Exchange Rate Sync Failed", {
              position: "top-right",
              autoClose: 5000,
              theme: "colored",
            });
          });
      })
      .catch(() => {
        setIsSyncing(false);
        toast.error("Exchange Rate Sync Failed", {
          position: "top-right",
          autoClose: 5000,
          theme: "colored",
        });
      });
  };

  return (
    <div className="admin-shell">
      <AsideNav />
      <div className="admin-main">
        <header className="admin-topbar">
          <p className="admin-topbar-title">{pageTitle}</p>
          <div className="admin-topbar-actions">
            {userId ? (
              <>
                <button
                  type="button"
                  onClick={handleSyncClick}
                  disabled={isSyncing}
                  className="admin-topbar-btn"
                >
                  {isSyncing ? "Syncing…" : "Sync exchange rate"}
                </button>
                <button
                  type="button"
                  onClick={logoutHandle}
                  className="admin-topbar-btn admin-topbar-btn--accent"
                >
                  Logout <IoIosLogOut className="text-lg" />
                </button>
              </>
            ) : (
              <button
                type="button"
                className="admin-topbar-btn admin-topbar-btn--accent"
                onClick={handleLoginClick}
              >
                Login
              </button>
            )}
          </div>
        </header>
        <main className="admin-content">
          <CrmActivityTracker />
          <AdminScreenGuard>
            <Outlet />
          </AdminScreenGuard>
        </main>
      </div>
    </div>
  );
};

function AdminScreenGuard({ children }) {
  const location = useLocation();
  const { loading, canView, firstAllowedPath } = usePermission();
  const code = screenCodeFromPath(location.pathname);

  if (loading) {
    return <p className="text-lg p-6">Checking permissions…</p>;
  }

  if (
    !code ||
    canView(code) ||
    (code === "setup.delivery-setup" && canAccessDeliverySetup(canView))
  ) {
    return children;
  }

  return <Navigate to={firstAllowedPath || "/admin-dashboard"} replace />;
}

export default AdminDashboard;
